import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Header } from '../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Coins, Plus, Search, ArrowUpRight, ArrowDownRight, Filter } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PointsPage() {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  
  // Adjust dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [adjustUser, setAdjustUser] = useState(null);
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser]);

  const fetchData = async () => {
    try {
      const params = selectedUser ? `?user_id=${selectedUser}` : '';
      const [transactionsRes, usersRes] = await Promise.all([
        axios.get(`${API}/points/transactions${params}`, { withCredentials: true }),
        axios.get(`${API}/users`, { withCredentials: true })
      ]);
      setTransactions(transactionsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const openAdjustDialog = (targetUser = null) => {
    setAdjustUser(targetUser);
    setAmount('');
    setDescription('');
    setDialogOpen(true);
  };

  const handleAdjust = async () => {
    if (!adjustUser || !amount || !description) {
      toast.error('Please fill all fields');
      return;
    }

    try {
      await axios.post(`${API}/points/${adjustUser.user_id}/adjust`, {
        amount: parseInt(amount),
        description
      }, { withCredentials: true });
      toast.success('Points adjusted');
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust points');
    }
  };

  const getTransactionIcon = (type) => {
    if (type === 'earn' || type === 'referral' || (type === 'adjust' && transactions.find(t => t.transaction_type === type)?.amount > 0)) {
      return <ArrowUpRight className="w-4 h-4 text-green-500" />;
    }
    return <ArrowDownRight className="w-4 h-4 text-red-500" />;
  };

  const getTransactionBadge = (type) => {
    const variants = {
      earn: 'default',
      spend: 'destructive',
      adjust: 'secondary',
      referral: 'default'
    };
    return <Badge variant={variants[type] || 'secondary'}>{type}</Badge>;
  };

  // Calculate stats
  const totalEarned = transactions
    .filter(t => t.amount > 0)
    .reduce((sum, t) => sum + t.amount, 0);
  const totalSpent = transactions
    .filter(t => t.amount < 0)
    .reduce((sum, t) => sum + Math.abs(t.amount), 0);

  return (
    <DashboardLayout>
      <Header title="Points" subtitle="Manage loyalty points across all users" />
      
      <div className="p-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card data-testid="total-earned-card">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-sm bg-green-500/10 flex items-center justify-center">
                <ArrowUpRight className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-500">+{totalEarned.toLocaleString()}</p>
                <p className="text-sm text-muted-foreground">Total Earned</p>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="total-spent-card">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-sm bg-red-500/10 flex items-center justify-center">
                <ArrowDownRight className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-500">-{totalSpent.toLocaleString()}</p>
                <p className="text-sm text-muted-foreground">Total Spent</p>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="transactions-count-card">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-sm bg-accent/10 flex items-center justify-center">
                <Coins className="w-6 h-6 text-accent" />
              </div>
              <div>
                <p className="text-2xl font-bold">{transactions.length}</p>
                <p className="text-sm text-muted-foreground">Transactions</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter & Actions */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4 items-end">
              <div className="flex-1 space-y-2">
                <Label>Filter by User</Label>
                <Select value={selectedUser} onValueChange={setSelectedUser}>
                  <SelectTrigger data-testid="user-filter-select">
                    <SelectValue placeholder="All users" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.user_id} value={u.user_id}>
                        {u.name} ({u.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={() => openAdjustDialog()} data-testid="adjust-points-btn">
                <Plus className="w-4 h-4 mr-2" />
                Adjust Points
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Transactions Table */}
        <Card>
          <CardHeader>
            <CardTitle className="font-secondary uppercase">Transaction History</CardTitle>
            <CardDescription>All point transactions across the platform</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">Loading...</TableCell>
                  </TableRow>
                ) : transactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No transactions found
                    </TableCell>
                  </TableRow>
                ) : (
                  transactions.map((txn) => {
                    const txnUser = users.find(u => u.user_id === txn.user_id);
                    return (
                      <TableRow key={txn.transaction_id} className="table-row-hover">
                        <TableCell className="text-sm">
                          {new Date(txn.created_at).toLocaleDateString()}
                          <span className="block text-xs text-muted-foreground">
                            {new Date(txn.created_at).toLocaleTimeString()}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs">
                              {txnUser?.name?.charAt(0) || '?'}
                            </div>
                            <span className="text-sm">{txnUser?.name || 'Unknown'}</span>
                          </div>
                        </TableCell>
                        <TableCell>{getTransactionBadge(txn.transaction_type)}</TableCell>
                        <TableCell className="max-w-xs truncate">{txn.description}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">{txn.source}</Badge>
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          <span className={txn.amount >= 0 ? 'text-green-500' : 'text-red-500'}>
                            {txn.amount >= 0 ? '+' : ''}{txn.amount}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Adjust Points Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="adjust-dialog">
          <DialogHeader>
            <DialogTitle>Adjust Points</DialogTitle>
            <DialogDescription>
              Add or deduct points from a user's balance
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select User</Label>
              <Select 
                value={adjustUser?.user_id || ''} 
                onValueChange={(val) => setAdjustUser(users.find(u => u.user_id === val))}
              >
                <SelectTrigger data-testid="adjust-user-select">
                  <SelectValue placeholder="Select a user" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.user_id} value={u.user_id}>
                      {u.name} - {u.points_balance} pts
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Amount (negative to deduct)</Label>
              <Input
                type="number"
                placeholder="e.g. 100 or -50"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                data-testid="adjust-amount-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                placeholder="Reason for adjustment"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                data-testid="adjust-description-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAdjust} data-testid="confirm-adjust-btn">Adjust Points</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
